import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapPin, Phone, Mail, Clock, MessageCircle, Search, ArrowLeft, ChevronLeft } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
const Contact = () => {
  const {
    user,
    profile
  } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('contact');

  // Contact form state
  const [contactData, setContactData] = useState({
    fullName: '',
    phone: '',
    email: profile?.email || '',
    inquiryType: '',
    message: '',
    agreeToTerms: false
  });
  const [contactLoading, setContactLoading] = useState(false);

  // Special search form state
  const [specialSearchData, setSpecialSearchData] = useState({
    materials_needed: '',
    quantities: '',
    budget: '',
    deadline: '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    fullName: profile?.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : '',
    message: '',
    agreeToTerms: false
  });
  const [specialSearchLoading, setSpecialSearchLoading] = useState(false);

  // Check for tab parameter in URL and set active tab
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'special-search') {
      setActiveTab('special-search');
    }
  }, [searchParams]);

  // Update contact form email when profile changes
  useEffect(() => {
    if (profile?.email && !contactData.email) {
      setContactData(prev => ({
        ...prev,
        email: profile.email
      }));
    }
  }, [profile?.email, contactData.email]);
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!contactData.fullName.trim()) {
      toast.error('יש למלא שם מלא');
      return;
    }
    if (!contactData.phone.trim()) {
      toast.error('יש למלא מספר טלפון');
      return;
    }
    if (!contactData.email.trim()) {
      toast.error('יש למלא כתובת אימייל');
      return;
    }
    if (!contactData.inquiryType) {
      toast.error('יש לבחור סוג פנייה');
      return;
    }
    if (!contactData.message.trim()) {
      toast.error('יש למלא הודעה');
      return;
    }
    if (!contactData.agreeToTerms) {
      toast.error('יש להסכים לתנאי השימוש ומדיניות הפרטיות');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactData.email)) {
      toast.error('כתובת האימייל אינה תקינה');
      return;
    }
    setContactLoading(true);
    try {
      // Import validation functions
      const {
        sanitizeInput,
        sanitizeHtml
      } = await import('@/lib/security');
      const {
        error
      } = await supabase.from('contact_requests').insert({
        user_id: user?.id || null,
        full_name: sanitizeInput(contactData.fullName),
        phone: sanitizeInput(contactData.phone),
        email: sanitizeInput(contactData.email),
        subject: sanitizeInput(contactData.inquiryType),
        inquiry_type: sanitizeInput(contactData.inquiryType),
        message: sanitizeHtml(contactData.message),
        status: 'pending'
      });
      if (error) throw error;
      toast.success('ההודעה נשלחה בהצלחה! נחזור אליך בהקרוב');
      setContactData({
        fullName: '',
        phone: '',
        email: profile?.email || '',
        inquiryType: '',
        message: '',
        agreeToTerms: false
      });
    } catch (error) {
      console.error('Error submitting contact request:', error);
      toast.error('שגיאה בשליחת ההודעה');
    } finally {
      setContactLoading(false);
    }
  };
  const handleSpecialSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Import validation functions
    const {
      validateSpecialSearchRequest,
      sanitizeInput,
      sanitizeHtml,
      sanitizeNumericInput
    } = await import('@/lib/security');

    // Validate the form data
    const validationResult = validateSpecialSearchRequest({
      ...specialSearchData,
      budget: specialSearchData.budget ? parseFloat(specialSearchData.budget) : null
    });
    if (!validationResult.isValid) {
      toast.error(validationResult.errors[0]);
      return;
    }

    if (!specialSearchData.agreeToTerms) {
      toast.error('יש להסכים לתנאי השימוש ומדיניות הפרטיות');
      return;
    }

    // Check rate limiting before submission
    const rateLimitOk = await supabase.rpc('check_request_rate_limit', {
      email_param: specialSearchData.email,
      request_type: 'special_search_request'
    });
    if (rateLimitOk.error || !rateLimitOk.data) {
      toast.error('יותר מדי בקשות מאותה כתובת מייל. אנא נסה שוב בעוד שעה.');
      setSpecialSearchLoading(false);
      return;
    }
    setSpecialSearchLoading(true);
    try {
      const {
        error
      } = await supabase.from('special_search_requests').insert({
        user_id: user?.id || null,
        materials_needed: sanitizeInput(specialSearchData.materials_needed),
        quantities: sanitizeInput(specialSearchData.quantities || ''),
        budget: sanitizeNumericInput(specialSearchData.budget || ''),
        deadline: specialSearchData.deadline || null,
        email: sanitizeInput(specialSearchData.email),
        phone: sanitizeInput(specialSearchData.phone || ''),
        full_name: sanitizeInput(specialSearchData.fullName || ''),
        message: sanitizeHtml(specialSearchData.message || ''),
        status: 'pending'
      });
      if (error) throw error;
      toast.success('בקשת החיפוש נשלחה בהצלחה! נחזור אליך בהקרוב');
      setSpecialSearchData({
        materials_needed: '',
        quantities: '',
        budget: '',
        deadline: '',
        email: profile?.email || '',
        phone: profile?.phone || '',
        fullName: profile?.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : '',
        message: '',
        agreeToTerms: false
      });
    } catch (error) {
      console.error('Error submitting special search request:', error);
      toast.error('שגיאה בשליחת הבקשה');
    } finally {
      setSpecialSearchLoading(false);
    }
  };
  return <div className="min-h-screen bg-background">
    <Navigation />

    <main className="pt-4 lg:pt-8 pb-12">
      <div className="container mx-auto px-4">
        {/* Breadcrumbs */}
        <div className="mb-6 mt-0" dir="rtl">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">
                  בית
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronLeft className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>צור קשר</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">צור קשר</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            יש לך שאלות? רוצה להצטרף אלינו? אנחנו כאן בשבילך
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Forms - Using Tabs */}
          <div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" dir="rtl">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="contact">שלח הודעה</TabsTrigger>
                <TabsTrigger value="special-search">מצא לי את המוצר</TabsTrigger>
              </TabsList>

              {/* Regular Contact Form */}
              <TabsContent value="contact">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">שלח לנו הודעה</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleContactSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">
                            שם מלא *
                          </label>
                          <Input value={contactData.fullName} onChange={e => setContactData(prev => ({
                            ...prev,
                            fullName: e.target.value
                          }))} placeholder="הכנס את שמך המלא" required />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">
                            טלפון *
                          </label>
                          <Input value={contactData.phone} onChange={e => setContactData(prev => ({
                            ...prev,
                            phone: e.target.value.replace(/\D/g, '').slice(0, 10)
                          }))} placeholder="05x-xxxxxxx" className="placeholder:text-muted-foreground/40 placeholder:text-xs text-right" dir="ltr" maxLength={10} required />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          אימייל *
                        </label>
                        <Input type="email" value={contactData.email} onChange={e => setContactData(prev => ({
                          ...prev,
                          email: e.target.value
                        }))} placeholder="your@email.com" required />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          סוג הפנייה *
                        </label>
                        <select value={contactData.inquiryType} onChange={e => setContactData(prev => ({
                          ...prev,
                          inquiryType: e.target.value
                        }))} className="w-full p-3 border border-border rounded-md bg-background" required>
                          <option value="">בחר סוג פנייה</option>
                          <option value="supplier">הצטרפות כספק</option>
                          <option value="buyer">שאלות קנייה</option>
                          <option value="technical">תמיכה טכנית</option>
                          <option value="business">פיתוח עסקי</option>
                          <option value="other">אחר</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          הודעה *
                        </label>
                        <Textarea value={contactData.message} onChange={e => setContactData(prev => ({
                          ...prev,
                          message: e.target.value
                        }))} placeholder="כתב כאן את הודעתך..." className="min-h-32" required />
                      </div>

                      <div className="flex items-start gap-2">
                        <input type="checkbox" id="privacy" checked={contactData.agreeToTerms} onChange={e => setContactData(prev => ({
                          ...prev,
                          agreeToTerms: e.target.checked
                        }))} className="mt-1" required />
                        <label htmlFor="privacy" className="text-sm text-muted-foreground">
                          אני מסכים ל<a href="/terms" target="_blank" rel="noopener noreferrer" className="text-lime hover:underline">תנאי השימוש</a> ו<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-lime hover:underline">מדיניות הפרטיות</a>
                        </label>
                      </div>

                      <Button type="submit" disabled={contactLoading} className="w-full bg-lime text-lime-foreground hover:bg-lime/90" size="lg">
                        <MessageCircle className="h-4 w-4 ml-2" />
                        {contactLoading ? 'שולח...' : 'שלח הודעה'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Special Product Search Form */}
              <TabsContent value="special-search">
                <Card dir="rtl">
                  <CardHeader className="text-right">
                    <CardTitle className="text-2xl flex items-center gap-2 text-right" dir="rtl" style={{
                      textAlign: 'right'
                    }}>
                      <Search className="h-6 w-6" />
                      מצא לי את המוצר
                    </CardTitle>
                    <p className="text-muted-foreground text-right" dir="rtl" style={{
                      textAlign: 'right'
                    }}>
                      לא מצאת את מה שאתה מחפש? ספר לנו מה אתה צריך ואנחנו נאתר עבורך
                    </p>
                  </CardHeader>
                  <CardContent dir="rtl">
                    <form onSubmit={handleSpecialSearchSubmit} className="space-y-6" dir="rtl">
                      <div className="text-right">
                        <Label htmlFor="materials_needed" className="text-right block mb-2" style={{
                          textAlign: 'right'
                        }}>
                          חומרים נדרשים <span className="text-red-500">*</span>
                        </Label>
                        <Textarea id="materials_needed" value={specialSearchData.materials_needed} onChange={e => setSpecialSearchData(prev => ({
                          ...prev,
                          materials_needed: e.target.value
                        }))} placeholder="תאר את החומרים שאתה מחפש: סוג, מידות, מאפיינים טכניים וכו'..." className="text-right min-h-24" dir="rtl" style={{
                          textAlign: 'right',
                          direction: 'rtl'
                        }} required />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-right">
                          <Label htmlFor="quantities" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>כמויות משוערות</Label>
                          <Input id="quantities" value={specialSearchData.quantities} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            quantities: e.target.value
                          }))} placeholder="לדוגמה: 100 מ״ר, 50 יח'" className="text-right" dir="rtl" style={{
                            textAlign: 'right',
                            direction: 'rtl'
                          }} />
                        </div>
                        <div className="text-right">
                          <Label htmlFor="budget" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>תקציב (₪)</Label>
                          <Input id="budget" type="number" value={specialSearchData.budget} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            budget: e.target.value
                          }))} placeholder="0" className="text-right" dir="rtl" style={{
                            textAlign: 'right',
                            direction: 'rtl'
                          }} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-right">
                          <Label htmlFor="deadline" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>מועד נדרש</Label>
                          <Input id="deadline" type="date" value={specialSearchData.deadline} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            deadline: e.target.value
                          }))} className="text-right" dir="rtl" />
                        </div>
                        <div className="text-right">
                          <Label htmlFor="search_email" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>
                            אימייל <span className="text-red-500">*</span>
                          </Label>
                          <Input id="search_email" type="email" value={specialSearchData.email} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            email: e.target.value
                          }))} placeholder="your@email.com" className="text-right" dir="rtl" style={{
                            textAlign: 'right',
                            direction: 'rtl'
                          }} required />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="text-right">
                          <Label htmlFor="search_phone" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>טלפון</Label>
                          <Input id="search_phone" type="tel" value={specialSearchData.phone} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            phone: e.target.value.replace(/\D/g, '').slice(0, 10)
                          }))} placeholder="05x-xxxxxxx" className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs" dir="ltr" style={{
                            textAlign: 'left',
                            direction: 'ltr'
                          }} maxLength={10} />
                        </div>
                        <div className="text-right">
                          <Label htmlFor="search_full_name" className="text-right block mb-2" style={{
                            textAlign: 'right'
                          }}>שם מלא</Label>
                          <Input id="search_full_name" type="text" value={specialSearchData.fullName} onChange={e => setSpecialSearchData(prev => ({
                            ...prev,
                            fullName: e.target.value
                          }))} placeholder="הכנס את שמך המלא" className="text-right" dir="rtl" style={{
                            textAlign: 'right',
                            direction: 'rtl'
                          }} />
                        </div>
                      </div>

                      <div className="text-right">
                        <Label htmlFor="search_message" className="text-right block mb-2" style={{
                          textAlign: 'right'
                        }}>פרטים נוספים</Label>
                        <Textarea id="search_message" value={specialSearchData.message} onChange={e => setSpecialSearchData(prev => ({
                          ...prev,
                          message: e.target.value
                        }))} placeholder="מידע נוסף על הפרויקט, דרישות מיוחדות, העדפות וכו'..." className="text-right min-h-20" dir="rtl" style={{
                          textAlign: 'right',
                          direction: 'rtl'
                        }} />
                      </div>

                      <div className="flex items-start gap-2">
                        <input type="checkbox" id="special-search-privacy" checked={specialSearchData.agreeToTerms} onChange={e => setSpecialSearchData(prev => ({
                          ...prev,
                          agreeToTerms: e.target.checked
                        }))} className="mt-1" required />
                        <label htmlFor="special-search-privacy" className="text-sm text-muted-foreground">
                          אני מסכים ל<a href="/terms" target="_blank" rel="noopener noreferrer" className="text-lime hover:underline">תנאי השימוש</a> ו<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-lime hover:underline">מדיניות הפרטיות</a>
                        </label>
                      </div>

                      <Button type="submit" disabled={specialSearchLoading} className="w-full bg-lime text-lime-foreground hover:bg-lime/90 flex-row-reverse" size="lg">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {specialSearchLoading ? 'שולח...' : 'שליחה'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* FAQ Section */}
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>שאלות נפוצות</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">איך אני יכול להצטרף כספק?</h4>
                    <p className="text-muted-foreground text-sm">
                      פנה אלינו דרך טופס הקשר או התקשר ישירות, ואנחנו נסביר לך על התהליך.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">מה זמני המשלוח?</h4>
                    <p className="text-muted-foreground text-sm">
                      זמני המשלוח משתנים בהתאם לספק ולמיקום. בדרך כלל 2-5 ימי עסקים.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">איך פועל התשלום?</h4>
                    <p className="text-muted-foreground text-sm">אנחנו מקבלים תשלומים באמצעות כרטיסי אשראי.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">איך עובד שירות החיפוש המיוחד?</h4>
                    <p className="text-muted-foreground text-sm">
                      ממלאים את הטופס עם פרטי החומרים הנדרשים, ואנחנו מאתרים עבורכם ספקים מתאימים תוך 24-48 שעות.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>;
};
export default Contact;